import { useEffect, useMemo, useState } from 'react'
import {
  isLegislationTypeCode,
  type LegislationTypeCode,
} from '@artem-lex/common-server/radar/legislationTypes'
import { Check, ExternalLink } from 'lucide-react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useGetUserSession } from '@/shared/hooks/api/auth'
import {
  useLegislationCorpus,
  type LegislationCorpusItem,
} from '@/shared/hooks/api/radarLegislationCorpus'
import { useScopeTierTags } from '@/shared/hooks/api/radarScopeTier'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import LegislationTypeFilter from '@/features/radar-scope-tier/components/LegislationTypeFilter'

const PAGE_SIZE = 50

const EMBEDDING_RELEASE_FILTER_VALUES = [
  'pending',
  'complete',
  'failed',
] as const
type EmbeddingReleaseFilterValue =
  (typeof EMBEDDING_RELEASE_FILTER_VALUES)[number]

function formatDate(value: string | null): string {
  if (!value) {
    return '—'
  }
  return value.slice(0, 10)
}

function formatDateTime(value: string | null): string {
  if (!value) {
    return '—'
  }
  return new Date(value).toLocaleString('en-GB')
}

function parsePositiveInt(raw: string | null): number | undefined {
  if (!raw?.trim()) {
    return undefined
  }
  const parsed = Number.parseInt(raw, 10)
  if (!Number.isFinite(parsed) || parsed < 1) {
    return undefined
  }
  return parsed
}

function parseCsvParam(raw: string | null): string[] {
  if (!raw?.trim()) {
    return []
  }
  return Array.from(
    new Set(
      raw
        .split(',')
        .map((value) => value.trim())
        .filter((value) => value.length > 0)
    )
  )
}

function parseTierParam(raw: string | null): Array<1 | 2 | 3> {
  return parseCsvParam(raw)
    .map((value) => Number.parseInt(value, 10))
    .filter(
      (value): value is 1 | 2 | 3 => value === 1 || value === 2 || value === 3
    )
}

function parseTypeParam(raw: string | null): LegislationTypeCode[] {
  return parseCsvParam(raw).filter(isLegislationTypeCode)
}

function parseEmbeddingParam(
  raw: string | null
): EmbeddingReleaseFilterValue[] {
  return parseCsvParam(raw).filter(
    (value): value is EmbeddingReleaseFilterValue =>
      value === 'pending' || value === 'complete' || value === 'failed'
  )
}

function joinCsv(values: string[]): string | null {
  if (values.length === 0) {
    return null
  }
  return values.join(',')
}

export default function RadarLegislationCorpusPage() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const { data: session, isFetched: isSessionFetched } = useGetUserSession()

  const searchQuery = params.get('q') ?? ''
  const inForceParam = params.get('inForceFilter')
  const inForceFilter =
    inForceParam === 'in_force' || inForceParam === 'not_in_force'
      ? inForceParam
      : 'all'
  const statusParam = params.get('status')
  const status =
    statusParam === 'pending' ||
    statusParam === 'approved' ||
    statusParam === 'flagged' ||
    statusParam === 'trashed'
      ? statusParam
      : 'all'
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1)
  const detectedWithinDays = parsePositiveInt(params.get('detectedWithinDays'))
  const legalEndWithinDays = parsePositiveInt(params.get('legalEndWithinDays'))
  const selectedTypes = useMemo(
    () => parseTypeParam(params.get('types')),
    [params]
  )
  const selectedTags = useMemo(
    () => parseCsvParam(params.get('tags')),
    [params]
  )
  const selectedTiers = useMemo(
    () => parseTierParam(params.get('tiers')),
    [params]
  )
  const selectedEmbeddingStatuses = useMemo(
    () => parseEmbeddingParam(params.get('embeddingReleaseStatus')),
    [params]
  )

  const [searchInput, setSearchInput] = useState(searchQuery)
  const [isTagMenuOpen, setIsTagMenuOpen] = useState(false)
  const [tagSearch, setTagSearch] = useState('')
  const tagOptionsQuery = useScopeTierTags({
    query: tagSearch,
    limit: 200,
  })

  useEffect(() => {
    setSearchInput(searchQuery)
  }, [searchQuery])

  useEffect(() => {
    if (isSessionFetched && session && !session.isArtemAdmin) {
      navigate('/', { replace: true })
    }
  }, [isSessionFetched, navigate, session])

  const corpusQuery = useLegislationCorpus(
    {
      search: searchQuery.trim() || undefined,
      inForceFilter,
      status,
      types: joinCsv(selectedTypes) ?? undefined,
      tags: joinCsv(selectedTags) ?? undefined,
      tiers: joinCsv(selectedTiers.map(String)) ?? undefined,
      embeddingReleaseStatus: joinCsv(selectedEmbeddingStatuses) ?? undefined,
      detectedWithinDays,
      legalEndWithinDays,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    },
    { enabled: isSessionFetched && session?.isArtemAdmin === true }
  )

  const total = corpusQuery.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const items = useMemo(
    () => corpusQuery.data?.items ?? [],
    [corpusQuery.data?.items]
  )

  const updateParams = (updates: Record<string, string | null>) => {
    const next = new URLSearchParams(params)
    for (const [key, value] of Object.entries(updates)) {
      if (!value?.trim()) {
        next.delete(key)
      } else {
        next.set(key, value)
      }
    }
    if (!Object.prototype.hasOwnProperty.call(updates, 'page')) {
      next.delete('page')
    }
    setParams(next)
  }

  const onApplySearch = () => {
    updateParams({ q: searchInput.trim() ? searchInput.trim() : null })
  }

  const toggleFilterType = (code: LegislationTypeCode) => {
    const next = selectedTypes.includes(code)
      ? selectedTypes.filter((value) => value !== code)
      : [...selectedTypes, code]
    updateParams({ types: joinCsv(next) })
  }

  const toggleFilterTag = (tagName: string) => {
    const next = selectedTags.includes(tagName)
      ? selectedTags.filter((value) => value !== tagName)
      : [...selectedTags, tagName]
    updateParams({ tags: joinCsv(next) })
  }

  const toggleTier = (tier: 1 | 2 | 3) => {
    const next = selectedTiers.includes(tier)
      ? selectedTiers.filter((value) => value !== tier)
      : [...selectedTiers, tier]
    updateParams({ tiers: joinCsv(next.map(String)) })
  }

  const toggleEmbeddingStatus = (statusValue: EmbeddingReleaseFilterValue) => {
    const next = selectedEmbeddingStatuses.includes(statusValue)
      ? selectedEmbeddingStatuses.filter((value) => value !== statusValue)
      : [...selectedEmbeddingStatuses, statusValue]
    updateParams({ embeddingReleaseStatus: joinCsv(next) })
  }

  if (session && !session.isArtemAdmin) {
    return null
  }

  const tagTriggerLabel =
    selectedTags.length > 0
      ? `Tags (${selectedTags.length} selected)`
      : 'Filter tags'

  return (
    <div className='flex min-h-0 flex-1 flex-col overflow-auto p-4 sm:p-6'>
      <div className='mb-6'>
        <h2 className='text-2xl font-bold tracking-tight'>
          Radar legislation corpus
        </h2>
        <p className='text-muted-foreground'>
          Browse all Radar CELEX with in-force status, scope review metadata,
          and flip history.
        </p>
      </div>

      <div className='flex flex-col gap-4'>
        <div className='flex flex-wrap items-end gap-3'>
          <div className='flex min-w-[240px] flex-1 flex-col gap-1'>
            <label className='text-sm font-medium' htmlFor='corpus-search'>
              Search
            </label>
            <div className='flex gap-2'>
              <Input
                id='corpus-search'
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    onApplySearch()
                  }
                }}
                placeholder='CELEX or title'
              />
              <Button type='button' variant='secondary' onClick={onApplySearch}>
                Apply
              </Button>
            </div>
          </div>

          <FilterSelect
            label='In force'
            value={inForceFilter}
            onChange={(value) =>
              updateParams({ inForceFilter: value === 'all' ? null : value })
            }
            options={[
              { value: 'all', label: 'All' },
              { value: 'in_force', label: 'In force' },
              { value: 'not_in_force', label: 'Out of force' },
            ]}
          />

          <FilterSelect
            label='Scope review'
            value={status}
            onChange={(value) =>
              updateParams({ status: value === 'all' ? null : value })
            }
            options={[
              { value: 'all', label: 'All' },
              { value: 'pending', label: 'Pending' },
              { value: 'approved', label: 'Approved' },
              { value: 'flagged', label: 'Flagged' },
              { value: 'trashed', label: 'Trashed' },
            ]}
          />

          <FilterSelect
            label='Detected within'
            value={detectedWithinDays ? String(detectedWithinDays) : 'any'}
            onChange={(value) =>
              updateParams({
                detectedWithinDays: value === 'any' ? null : value,
              })
            }
            options={[
              { value: 'any', label: 'Any time' },
              { value: '7', label: '7 days' },
              { value: '14', label: '14 days' },
              { value: '30', label: '30 days' },
            ]}
          />

          <FilterSelect
            label='End of validity within'
            value={legalEndWithinDays ? String(legalEndWithinDays) : 'any'}
            onChange={(value) =>
              updateParams({
                legalEndWithinDays: value === 'any' ? null : value,
              })
            }
            options={[
              { value: 'any', label: 'Any time' },
              { value: '7', label: '7 days' },
              { value: '14', label: '14 days' },
              { value: '30', label: '30 days' },
            ]}
          />
        </div>

        <div className='grid gap-3 md:grid-cols-2 xl:grid-cols-4'>
          <div className='flex flex-col gap-1'>
            <span className='text-sm font-medium'>Legislation type</span>
            <LegislationTypeFilter
              selectedTypes={selectedTypes}
              onToggle={toggleFilterType}
              onClear={() => updateParams({ types: null })}
            />
          </div>

          <div className='flex flex-col gap-1'>
            <span className='text-sm font-medium'>Tags</span>
            <Popover open={isTagMenuOpen} onOpenChange={setIsTagMenuOpen}>
              <PopoverTrigger asChild>
                <button
                  type='button'
                  className='flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 text-left text-sm shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50'
                >
                  <span
                    className={
                      selectedTags.length > 0 ? '' : 'text-muted-foreground'
                    }
                  >
                    {tagTriggerLabel}
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent align='start' className='w-[320px] p-0'>
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder='Search tags…'
                    value={tagSearch}
                    onValueChange={setTagSearch}
                  />
                  <CommandList>
                    <CommandEmpty>No tags found.</CommandEmpty>
                    {(tagOptionsQuery.data?.items ?? []).map((tag) => {
                      const isSelected = selectedTags.includes(tag.name)
                      return (
                        <CommandItem
                          key={tag.normalized}
                          value={tag.name}
                          onSelect={() => toggleFilterTag(tag.name)}
                        >
                          <span className='flex-1'>{tag.name}</span>
                          {isSelected ? (
                            <Check className='size-4 text-muted-foreground' />
                          ) : null}
                        </CommandItem>
                      )
                    })}
                  </CommandList>
                </Command>
                {selectedTags.length > 0 ? (
                  <div className='border-t p-2'>
                    <button
                      type='button'
                      className='text-xs underline'
                      onClick={() => updateParams({ tags: null })}
                    >
                      Clear tag filters
                    </button>
                  </div>
                ) : null}
              </PopoverContent>
            </Popover>
          </div>

          <div className='flex flex-col gap-1'>
            <span className='text-sm font-medium'>Scope tier</span>
            <div className='flex min-h-9 flex-wrap items-center gap-3 rounded-md border px-3 py-2'>
              {[1, 2, 3].map((tierValue) => (
                <label
                  key={tierValue}
                  className='flex items-center gap-1 text-sm'
                >
                  <input
                    type='checkbox'
                    checked={selectedTiers.includes(tierValue as 1 | 2 | 3)}
                    onChange={() => toggleTier(tierValue as 1 | 2 | 3)}
                  />
                  <span>{`Tier ${tierValue}`}</span>
                </label>
              ))}
              {selectedTiers.length > 0 ? (
                <button
                  type='button'
                  className='text-xs underline'
                  onClick={() => updateParams({ tiers: null })}
                >
                  Clear
                </button>
              ) : null}
            </div>
          </div>

          <div className='flex flex-col gap-1'>
            <span className='text-sm font-medium'>Embedding release</span>
            <div className='flex min-h-9 flex-wrap items-center gap-3 rounded-md border px-3 py-2'>
              {EMBEDDING_RELEASE_FILTER_VALUES.map((statusValue) => (
                <label
                  key={statusValue}
                  className='flex items-center gap-1 text-sm'
                >
                  <input
                    type='checkbox'
                    checked={selectedEmbeddingStatuses.includes(statusValue)}
                    onChange={() => toggleEmbeddingStatus(statusValue)}
                  />
                  <span className='capitalize'>{statusValue}</span>
                </label>
              ))}
              {selectedEmbeddingStatuses.length > 0 ? (
                <button
                  type='button'
                  className='text-xs underline'
                  onClick={() => updateParams({ embeddingReleaseStatus: null })}
                >
                  Clear
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <Alert variant='warning' className='mt-4'>
        <AlertDescription>
          <p>
            <strong>In force (now)</strong> is the current Radar state used for
            retrieval.
            <br />
            <strong>Flip logged at</strong> is when sync recorded an
            out-of-force transition in the append-only flip log.
          </p>
          <p>
            For recent flips still affecting retrieval, use{' '}
            <strong>Detected within</strong> together with{' '}
            <strong>Out of force</strong>.
          </p>
        </AlertDescription>
      </Alert>

      <div className='mt-4 rounded-md border'>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>CELEX</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>In force (now)</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Tags</TableHead>
              <TableHead>Scope status</TableHead>
              <TableHead>Tier</TableHead>
              <TableHead>Flip logged at</TableHead>
              <TableHead>End of validity</TableHead>
              <TableHead>Embedding</TableHead>
              <TableHead className='whitespace-nowrap'>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {corpusQuery.isLoading && (
              <TableRow>
                <TableCell colSpan={11} className='text-muted-foreground'>
                  Loading corpus…
                </TableCell>
              </TableRow>
            )}
            {!corpusQuery.isLoading && corpusQuery.isError && (
              <TableRow>
                <TableCell colSpan={11} className='text-destructive'>
                  Failed to load corpus.
                </TableCell>
              </TableRow>
            )}
            {!corpusQuery.isLoading &&
              !corpusQuery.isError &&
              items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} className='text-muted-foreground'>
                    No matching CELEX.
                  </TableCell>
                </TableRow>
              )}
            {items.map((item) => (
              <CorpusRow key={item.documentId} item={item} />
            ))}
          </TableBody>
        </Table>
      </div>

      <div className='mt-4 flex items-center justify-between gap-3'>
        <p className='text-sm text-muted-foreground'>
          {total} total · page {page} of {totalPages}
        </p>
        <div className='flex gap-2'>
          <Button
            type='button'
            variant='outline'
            disabled={page <= 1}
            onClick={() => updateParams({ page: String(page - 1) })}
          >
            Previous
          </Button>
          <Button
            type='button'
            variant='outline'
            disabled={page >= totalPages}
            onClick={() => updateParams({ page: String(page + 1) })}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  )
}

function buildScopeTierUrl(documentId: string, celex: string): string {
  const params = new URLSearchParams({
    documentId,
    q: primaryCelexForSearch(celex),
    inForceFilter: 'all',
    includeUnassigned: 'true',
    includeTrashed: 'true',
  })
  return `/radar/scope-tier?${params.toString()}`
}

function primaryCelexForSearch(celex: string): string {
  const parts = celex
    .split('__')
    .map((part) => part.trim())
    .filter(Boolean)
  if (parts.length >= 2 && parts[0] === parts[1]) {
    return parts[0] ?? celex
  }
  return parts[0] ?? celex
}

function CorpusRow({ item }: { item: LegislationCorpusItem }) {
  const scopeTierUrl = buildScopeTierUrl(item.documentId, item.celex)
  const tagsLabel = item.tags.length > 0 ? item.tags.join(', ') : '—'

  return (
    <TableRow>
      <TableCell className='font-mono text-xs'>{item.celex}</TableCell>
      <TableCell
        className='max-w-[240px] truncate'
        title={item.title ?? undefined}
      >
        {item.title ?? '—'}
      </TableCell>
      <TableCell>{item.inForce ? 'Yes' : 'No'}</TableCell>
      <TableCell>{item.type ?? '—'}</TableCell>
      <TableCell className='max-w-[180px] truncate' title={tagsLabel}>
        {tagsLabel}
      </TableCell>
      <TableCell>{item.scopeReviewStatus}</TableCell>
      <TableCell>{item.currentTier ?? '—'}</TableCell>
      <TableCell>{formatDateTime(item.flipDetectedAt)}</TableCell>
      <TableCell>{formatDate(item.flipLegalEndDate)}</TableCell>
      <TableCell>{item.embeddingReleaseStatus ?? '—'}</TableCell>
      <TableCell className='whitespace-nowrap'>
        <div className='flex items-center gap-2'>
          {item.eurlexUrl ? (
            <Button asChild size='sm' variant='outline'>
              <a href={item.eurlexUrl} target='_blank' rel='noreferrer'>
                EUR-Lex
                <ExternalLink className='ms-1 size-3' />
              </a>
            </Button>
          ) : null}
          <Button asChild size='sm' variant='secondary'>
            <Link to={scopeTierUrl}>Scope tier</Link>
          </Button>
        </div>
      </TableCell>
    </TableRow>
  )
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  options: Array<{ value: string; label: string }>
}) {
  const id = label.toLowerCase().replace(/\s+/g, '-')

  return (
    <div className='flex min-w-[160px] flex-col gap-1'>
      <label className='text-sm font-medium' htmlFor={id}>
        {label}
      </label>
      <Select
        value={value}
        onValueChange={(nextValue) => {
          if (nextValue) {
            onChange(nextValue)
          }
        }}
        items={options}
      >
        <SelectTrigger id={id}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
